import { createContext, forwardRef, useContext, useRef, useState } from 'react';
import { Slot } from '@radix-ui/react-slot';
import * as Primitive from '@radix-ui/react-tooltip';
import composeRefs from '@seznam/compose-react-refs';
import { AnimatePresence, cubicBezier, motion } from 'motion/react';

import { cn } from '@components/primitives/utilities';

import css from './styles.module.css';

const TooltipContext = createContext<boolean>(true);

export type TooltipProps = {
  content: React.ReactNode;
  children: React.ReactNode;
  interactive?: boolean;
  className?: string;
  side?: Primitive.TooltipContentProps['side'];
  align?: Primitive.TooltipContentProps['align'];
  sideOffset?: Primitive.TooltipContentProps['sideOffset'];
  alignOffset?: Primitive.TooltipContentProps['alignOffset'];
  wordbreak?: boolean;
  closeOnActive?: boolean;
  /**
   * The delay in milliseconds before the tooltip is shown.
   * @default 0
   */
  delay?: number;
  /**
   * Whether several tooltips can be nested inside each other.
   * If disabled, only the parent tooltip will be shown.
   * @default true
   */
  allowNesting?: boolean;
};

type ContentProps = React.ComponentProps<typeof Primitive.Content>;
type OnPointerDownOutsideHandler = ContentProps['onPointerDownOutside'];

export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  function Tooltip(
    {
      className,
      content,
      children,
      interactive = false,
      side = 'bottom',
      align = 'center',
      sideOffset = 8,
      alignOffset = -8,
      wordbreak,
      closeOnActive = true,
      allowNesting = true,
      delay = 0,
      ...props
    },
    ref
  ) {
    const trigger = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    let child = children;

    if (typeof children === 'string') {
      child = <span>{children}</span>;
    }

    const parentAllowsNesting = useContext(TooltipContext);
    if (!content || !parentAllowsNesting) {
      return (
        <Slot ref={ref} {...props}>
          {child}
        </Slot>
      );
    }

    const onPointerDownOutside: OnPointerDownOutsideHandler = (e) => {
      const target = e.target as HTMLElement;

      if (!target || !trigger.current) return;

      if (!closeOnActive && trigger.current.contains(target)) {
        e.preventDefault();
      }
    };

    function onTriggerClick(e: React.MouseEvent<HTMLButtonElement>) {
      if (!closeOnActive) {
        e.preventDefault();
      }
    }

    function onTriggerPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
      if (!closeOnActive) {
        e.preventDefault();
      }
    }

    return (
      <TooltipContext.Provider value={allowNesting}>
        <Primitive.Root
          open={open}
          onOpenChange={setOpen}
          delayDuration={delay}
          disableHoverableContent={!interactive}
        >
          <Primitive.Trigger
            ref={composeRefs(ref, trigger)}
            onClick={onTriggerClick}
            onPointerDown={onTriggerPointerDown}
            data-has-tooltip
            asChild
            {...props}
          >
            {child}
          </Primitive.Trigger>

          <AnimatePresence>
            {open && (
              <Primitive.Portal forceMount>
                <Primitive.Content
                  side={side}
                  align={align}
                  sideOffset={sideOffset}
                  alignOffset={alignOffset}
                  collisionPadding={10}
                  onPointerDownOutside={onPointerDownOutside}
                  asChild
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{
                      duration: 0.25,
                      ease: cubicBezier(0.16, 1, 0.3, 1),
                    }}
                    className={cn(css.content, className)}
                    data-wordbreak={wordbreak}
                  >
                    {content}
                  </motion.div>
                </Primitive.Content>
              </Primitive.Portal>
            )}
          </AnimatePresence>
        </Primitive.Root>
      </TooltipContext.Provider>
    );
  }
);
